/* eslint-disable @next/next/no-img-element */
import type { FC, FormEvent, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { BlogPost } from '@/data/blog'
import type { SupportArticle } from '@/data/support'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { Layout, Seo, PageHero } from '@/components/generals'
import { routes } from '@/lib/routes'
import { getBlogPosts } from '@/data/blog'
import { getSupportArticles } from '@/data/support'

type SearchPageProps = {
    posts: BlogPost[]
    articles: SupportArticle[]
}

const SearchPage: FC<SearchPageProps> = ({ posts, articles }): ReactNode => {
    const router = useRouter()
    const [term, setTerm] = useState('')

    useEffect(() => {
        const queryValue = router.query.query
        if (typeof queryValue === 'string') setTerm(queryValue)
    }, [router.query.query])

    const needle = term.trim().toLowerCase()
    const postResults = needle
        ? posts.filter(post => `${post.name} ${post.summary}`.toLowerCase().includes(needle))
        : []
    const supportResults = needle
        ? articles.filter(article => `${article.name} ${article.content}`.toLowerCase().includes(needle))
        : []
    const total = postResults.length + supportResults.length

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        router.replace({ pathname: routes.search, query: needle ? { query: needle } : {} }, undefined, { shallow: true })
    }

    return (
        <Layout>
            <Seo title='Search' description='Search articles and support across San Diego.' />

            <PageHero
                badge='Search'
                eyebrow='Find what you need'
                eyebrowHref={routes.support}
                title={
                    <>
                        Search <span className='text-gradient'>San Diego</span>
                    </>
                }
                description='Search across the blog and help center to find answers fast.'
            />

            <div className='section'>
                <div className='container'>
                    <div className='support-grid-layout'>
                        <div id='w-node-a70f84fd-27d8-6687-cecb-85c2c112e7e0-77af58ae' className='support-navigation-holder'>
                            <div className='support-nav'>
                                <form action={routes.search} method='get' className='search-form w-form' onSubmit={onSubmit}>
                                    <input
                                        className='search-input w-input'
                                        maxLength={256}
                                        name='query'
                                        placeholder='Search…'
                                        type='search'
                                        value={term}
                                        onChange={event => setTerm(event.target.value)}
                                        aria-label='Search'
                                    />
                                    <input type='submit' className='search-button w-button' value='' />
                                </form>
                            </div>
                        </div>

                        <div id='w-node-a70f84fd-27d8-6687-cecb-85c2c112e7f2-77af58ae' className='support-article-holder'>
                            <div className='small-container'>
                                {needle === '' && (
                                    <div className='text-center'>
                                        <img
                                            src='/images/Logo-About.png'
                                            loading='lazy'
                                            sizes='(max-width: 479px) 72vw, (max-width: 767px) 36vw, 200px'
                                            srcSet='/images/Logo-About-p-500.png 500w, /images/Logo-About-p-800.png 800w, /images/Logo-About.png 956w'
                                            alt='San Diego'
                                            className='support-app-icon'
                                        />
                                        <h2 className='heading-3-style'>
                                            Search <span className='text-gradient'>San Diego</span>
                                        </h2>
                                        <div className='w-layout-grid grid-10-columns'>
                                            <p className='grey-text'>Type a keyword above to search articles and help center answers.</p>
                                        </div>
                                    </div>
                                )}

                                {needle !== '' && (
                                    <>
                                        <h2 className='heading-3-style'>
                                            {total} result{total === 1 ? '' : 's'} for &ldquo;{term}&rdquo;
                                        </h2>

                                        {postResults.length > 0 && (
                                            <>
                                                <p className='grey-text'>
                                                    <strong>Articles</strong>
                                                </p>
                                                <div className='collection-list-wrapper'>
                                                    <div role='list' className='w-dyn-items'>
                                                        {postResults.map(post => (
                                                            <div key={post.slug} role='listitem' className='w-dyn-item'>
                                                                <Link href={routes.blogPost(post.slug)} className='list-item-card w-inline-block'>
                                                                    <div className='list-item-text'>{post.name}</div>
                                                                </Link>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {supportResults.length > 0 && (
                                            <>
                                                <p className='grey-text'>
                                                    <strong>Help center</strong>
                                                </p>
                                                <div className='collection-list-wrapper'>
                                                    <div role='list' className='w-dyn-items'>
                                                        {supportResults.map(article => (
                                                            <div key={article.slug} role='listitem' className='w-dyn-item'>
                                                                <Link href={routes.supportArticle(article.slug)} className='list-item-card w-inline-block'>
                                                                    <div className='list-item-text'>{article.name}</div>
                                                                </Link>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {total === 0 && <p className='grey-text'>No results found. Try a different keyword.</p>}
                                    </>
                                )}
                            </div>
                        </div>

                        <div id='w-node-a70f84fd-27d8-6687-cecb-85c2c112e803-77af58ae' className='support-cta-holder'>
                            <h2 className='card-title-sm'>Still need a hand ?</h2>
                            <p className='grey-text'>Our team is here to help. Reach out and we will get back to you quickly.</p>
                            <Link href={routes.contact} className='button w-button'>
                                Contact us
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<SearchPageProps> = async () => {
    const [posts, articles] = await Promise.all([getBlogPosts(), getSupportArticles()])
    return { props: { posts, articles }, revalidate: 60 }
}

export default SearchPage
